import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';

/**
 * `04.ComprobantePagos` — individual payment receipts. Each row maps to
 * one "Registrar pago" or "Registrar Promesa de Pago" action on the
 * Pagos screen. Admin in Administración later confirms the row
 * (`Status_CP: Pendiente de Confirmacion → Confirmado`), at which point
 * the header `Status_RO` advances via the Aprobaciones module.
 *
 * SCHEMA QUIRK: the internal field names for Anio_CP, MesAnio_CP,
 * Hora_CP and Status_CP are actually `Anio_P`, `MesAnio_P`, `Hora_P`,
 * `Status_P` — the displayed column name has a _CP suffix but the
 * underlying SharePoint static name is legacy _P. Graph writes/reads
 * use the INTERNAL name, so we expose those here. Confirmed via
 * `inspectList.ts ComprobantePagos`.
 */
export type ComprobantePagosFields = {
  Title?: string | null;
  IDOperacion_CP?: string | null;
  MedioPago_CP?: string | null;
  IDComprobante_CP?: string | null;
  Monto_CP?: string | null;
  Pesos_CP?: string | null;
  TipoCambio_CP?: string | null;
  MontoFactura_CP?: string | null;
  /** Importe declarado a ARCA al emitir (pesos si CbteMonedaId_CP es null; USD en Factura/NC E). */
  CbteImporte_CP?: string | null;
  /** Moneda del comprobante ARCA ('DOL' = dólares, Factura/NC E); null = pesos. */
  CbteMonedaId_CP?: string | null;
  /** NC de un ciclo de factura ya cerrado por una NC total: historia, no resta del saldo actual. */
  NcSuperseded_CP?: 'SI' | 'NO' | null;
  Fecha_CP?: string | null;
  FechaCobro_CP?: string | null;
  FechaPago_CP?: string | null;
  Mes_CP?: string | null;
  /** Internal name is `Anio_P` — displayed as `Anio_CP`. */
  Anio_P?: string | null;
  /** Internal name is `MesAnio_P` — displayed as `MesAnio_CP`. */
  MesAnio_P?: string | null;
  /** Internal name is `Hora_P` — displayed as `Hora_CP`. */
  Hora_P?: string | null;
  /** 'dd/mm/yyyy HH:mm' (hora local): cuándo se cargó el comprobante. */
  FechaHoraCarga_CP?: string | null;
  IVA_CP?: string | null;
  PorcentajeIVA_CP?: string | null;
  Facturado_CP?: string | null;
  NroFactura_CP?: string | null;
  ContieneFactura_CP?: 'SI' | 'NO' | string | null;
  EsPagoFinal_CP?: 'Si' | 'No' | string | null;
  Cliente_CP?: string | null;
  VersionApp_CP?: string | null;
  /** Raw PG primary key — stable across id spaces; used to link NC rows to their parent factura. */
  IdInterno_CP?: string | null;
  /** AR Nota de Crédito → the parent factura's `IdInterno_CP` (PK). Null on every non-NC row. */
  NotaCreditoDeId_CP?: string | null;
  /** AR Nota de Crédito → the credited factura's número, snapshotted at emit (survives the parent's reopen). Null on non-NC rows. */
  FacturaAsociadaNro_CP?: string | null;
  /**
   * Internal name is `Status_P` — displayed as `Status_CP`. Values:
   *   'Pendiente de Cobro'        — promesa de pago, not yet executed
   *   'Pendiente de Confirmacion' — pago registered, awaiting admin confirm
   *   'Confirmado'                — admin approved, monto counts toward paid total
   *   'Anulado'                   — soft-deleted
   */
  Status_P?:
    | 'Pendiente de Cobro'
    | 'Pendiente de Confirmacion'
    | 'Confirmado'
    | 'Anulado'
    | string
    | null;
  /**
   * MX-only Nova flow markers, server-managed.
   * `EsFacturaInterna_CP === 'SI'` flags the auto-generated Factura
   * Interna ("Valor Contable" invoice). One per f_nova venta.
   * `IDNova_CP` carries the PG id of that Factura Interna for the
   * operator-entered comprobantes and Anticipos that mirror them.
   */
  EsFacturaInterna_CP?: 'SI' | 'NO' | null;
  /**
   * AR "Facturación Previa": un admin autorizó emitir la factura ANTES del
   * cobro. Server-managed — sólo lo escribe el handler de aprobación.
   */
  FacturacionPrevia_CP?: 'SI' | 'NO' | null;
  IDNova_CP?: string | null;
  /** Factura PDF admin issued to the client. `ContieneFactura_CP` is the flag companion. */
  FacturaDriveItemID_CP?: string | null;
  FacturaFileName_CP?: string | null;
  /**
   * MX-only Factura Anticipada — uploaded first when the cobro is an
   * Anticipo (`EsPagoFinal_CP!='SI'`) that requires factura
   * (`ContieneFactura_CP='SI'`). The Final factura is then uploaded
   * into the regular `FacturaDriveItemID_CP` pair. Always null on AR
   * and on MX pago-final cobros.
   */
  FacturaAnticipadaDriveItemID_CP?: string | null;
  FacturaAnticipadaFileName_CP?: string | null;
  NroFacturaAnticipada_CP?: string | null;
  /** Proof-of-payment file the sales rep attached when registering the pago. */
  ComprobanteDriveItemID_CP?: string | null;
  ComprobanteFileName_CP?: string | null;
};

export type ComprobantePagoItem = ListItem<ComprobantePagosFields>;

export const comprobantePagosClient = new SharePointListClient<ComprobantePagosFields>(
  'ComprobantePagos',
);

/**
 * List every comprobante (any status) for an operación. `IDOperacion_CP`
 * is indexed so the filter is cheap.
 */
export async function listComprobantesByOperacion(
  operacionId: string | number,
): Promise<ComprobantePagoItem[]> {
  const items = await comprobantePagosClient.listItems({
    filter: `fields/IDOperacion_CP eq ${odataEscape(String(operacionId))}`,
  });
  return items.sort((a, b) => Number(a.id) - Number(b.id));
}

/**
 * Active = not anulado, AND not part of the MX Nova back-office flow.
 * Used to populate the operator-facing "Carga de pagos" table on the
 * Pagos / EditarCotizacion / VerFacturas screens. The Nova flow is an
 * independent track that lives entirely in the Administración module:
 *
 *  - `EsFacturaInterna_CP === 'SI'`     — auto-generated Italmexy → Nova
 *                                         "Valor Contable" invoice.
 *  - `MedioPago_CP === 'Cuentas por …'` — auto-generated Anticipo
 *                                         awaiting Nova's cobro confirm.
 *
 * Hiding both keeps the Pagos UX identical to AR for the operator: they
 * see only the comprobantes they themselves uploaded.
 */
export async function listActiveComprobantesByOperacion(
  operacionId: string | number,
): Promise<ComprobantePagoItem[]> {
  const all = await listComprobantesByOperacion(operacionId);
  return all.filter((c) => {
    if (c.fields.Status_P === 'Anulado') return false;
    // AR Nota de Crédito rows are fiscal-only credit notes stored on this list;
    // they carry a positive Monto_CP but are NOT operator cobros. Exclude them
    // from every consumer (Pagos/EditarCotización money sums + Carga de Pagos
    // table) so they stay inert to every aggregation.
    if (c.fields.Status_P === 'Nota de Credito') return false;
    if (c.fields.EsFacturaInterna_CP === 'SI') return false;
    if (c.fields.MedioPago_CP === 'Cuentas por Cobrar - Nova') return false;
    if (c.fields.MedioPago_CP === 'Cuentas por Pagar - Nova') return false;
    return true;
  });
}
